import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Next, Delete1, Prev } from '../../../../../assets/images';
import { WalkIcon, CaloryIcon, TimeIcon, DidimIcon } from '../../../../../assets/images/activityIcons';
import CommonCard from '../../../../../components/card/CommonCard';
import DonutChart from '../../../../../components/charts/donutChart';
import ExerciseDataItem from '../../exerciseDataItem';
import { EXERCISE_TARGETS } from '../../../../../constants';
import { calculatePercentage, formatDateTimeDisplay } from '../../../../../utils/base';

const DailyDetailCard = ({
    selectedDate,
    selectedMonth,
    selectedYear,
    totalSteps = 0,
    calories = 0,
    totalTime = 0,
    isDidim = false,
    showPrevious = true,
    showNext = true,
    onClose,
    onPrevious,
    onNext,
    sx = {},
    ...props
}) => {
    const theme = useTheme();
    const { t } = useTranslation();

    if (!selectedDate) return null;

    const dayNames = [t('sun'), t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')];
    const selectedDay = new Date(selectedYear, selectedMonth - 1, selectedDate);
    const dayName = dayNames[selectedDay.getDay()];

    return (
        <CommonCard
            sx={{
                width: '100%',
                backgroundColor: theme.palette.figma.block,
                ...sx,
            }}
            {...props}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Box sx={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {showPrevious && (
                            <Prev onClick={onPrevious} style={{ cursor: 'pointer' }} />
                        )}
                    </Box>
                    <Typography
                        sx={{
                            fontSize: '16px',
                            fontWeight: 500,
                            letterSpacing: '-0.48px',
                        }}
                    >
                        {formatDateTimeDisplay(selectedDay, 'monthDay')} ({dayName})
                    </Typography>
                    <Box sx={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {showNext && (
                            <Next onClick={onNext} style={{ cursor: 'pointer' }} />
                        )}
                    </Box>
                </Box>
                <Delete1 onClick={onClose} />
            </Box>

            {/* Content */}
            <Box
                sx={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'center',
                    width: '100%',
                }}
            >
                {/* Chart/Icon Area */}
                <DonutChart data={[
                    {
                        label: t('didim'),
                        value: calculatePercentage(
                            totalSteps,
                            EXERCISE_TARGETS.STEPS
                        ),
                        color: isDidim ? theme.palette.figma.didim : theme.palette.figma.steps,
                        radius: 45,
                        strokeWidth: 10,
                    },
                    {
                        label: t('calories'),
                        value: calculatePercentage(
                            calories,
                            EXERCISE_TARGETS.CALORIES
                        ),
                        color: theme.palette.figma.calorieRed,
                        radius: 35,
                        strokeWidth: 10,
                    },
                    {
                        label: t('time'),
                        value: calculatePercentage(
                            totalTime,
                            EXERCISE_TARGETS.ACTIVITY_TIME
                        ),
                        color: theme.palette.figma.time,
                        radius: 25,
                        strokeWidth: 10,
                    },
                ]}
                    size={100}
                    centerRadius={60} />

                {/* Data Items */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '5px',
                        flex: 1,
                    }}
                >
                    <ExerciseDataItem
                        icon={isDidim ? DidimIcon : WalkIcon}
                        value={totalSteps}
                        unit={t('didim')}
                        size="small"
                    />
                    <ExerciseDataItem
                        icon={CaloryIcon}
                        value={calories}
                        unit="kcal"
                        size="small"
                    />
                    <ExerciseDataItem
                        icon={TimeIcon}
                        value={totalTime}
                        unit={t('min')}
                        size="small"
                    />
                </Box>
            </Box>
        </CommonCard>
    );
};

export default DailyDetailCard;
